import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Calendar, Search, Plus, Eye, FileText, Trash2, X, Check, ChevronLeft,
  ChevronRight, Loader, Phone, Printer, RefreshCw, Camera, Image as ImageIcon, Link as LinkIcon, Car
} from 'lucide-react';
import api from '../../utils/api';

// ── Image resize utility (compresses before storing) ──────────────────────
async function resizeImage(file, maxW = 1200, quality = 0.82) {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(maxW / img.width, 1);
        const canvas = document.createElement('canvas');
        canvas.width  = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const STATUS_COLORS = {
  pending:     'bg-amber-100 text-amber-800 border-amber-200',
  confirmed:   'bg-blue-100 text-blue-800 border-blue-200',
  in_progress: 'bg-orange-100 text-orange-800 border-orange-200',
  completed:   'bg-green-100 text-green-800 border-green-200',
  invoiced:    'bg-purple-100 text-purple-800 border-purple-200',
  cancelled:   'bg-red-100 text-red-800 border-red-200',
  paid:        'bg-green-100 text-green-800 border-green-200',
  unpaid:      'bg-red-100 text-red-800 border-red-200',
  'n/a':       'bg-gray-100 text-gray-600 border-gray-200',
};

const DATE_TABS = [
  { id: 'yesterday',  label: 'Yesterday'  },
  { id: 'today',      label: 'Today'      },
  { id: 'tomorrow',   label: 'Tomorrow'   },
  { id: 'this_week',  label: 'This Week'  },
  { id: 'this_month', label: 'This Month' },
  { id: '',           label: 'All Time'   },
];

function Badge({ label }) {
  const cls = STATUS_COLORS[label?.toLowerCase()] || 'bg-gray-100 text-gray-600 border-gray-200';
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${cls}`}>{label}</span>;
}

function Modal({ open, onClose, title, children, maxWidth = 'max-w-2xl' }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded-2xl w-full ${maxWidth} max-h-[92vh] flex flex-col shadow-2xl my-2`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="font-black text-apex-blue-700 text-lg">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls  = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500 focus:ring-1 focus:ring-apex-blue-500";
const selectCls = `${inputCls} bg-white`;

function TimePicker({ value, onChange }) {
  const parse = v => {
    if (!v) return { h:'08', m:'00', p:'AM' };
    const [hh, mm] = v.split(':');
    const h24 = parseInt(hh);
    return { h: String(h24===0?12:h24>12?h24-12:h24).padStart(2,'0'), m: mm||'00', p: h24>=12?'PM':'AM' };
  };
  const fmt = (h, m, p) => {
    let h24 = parseInt(h);
    if (p==='PM' && h24!==12) h24 += 12;
    if (p==='AM' && h24===12) h24 = 0;
    return `${String(h24).padStart(2,'0')}:${m}`;
  };
  const { h, m, p } = parse(value);
  return (
    <div className="flex gap-2 items-center">
      <select value={h} onChange={e => onChange(fmt(e.target.value, m, p))} className={`${inputCls} flex-1`}>
        {['06','07','08','09','10','11','12','01','02','03','04','05'].map(v => <option key={v}>{v}</option>)}
      </select>
      <span className="font-bold text-gray-400">:</span>
      <select value={m} onChange={e => onChange(fmt(h, e.target.value, p))} className={`${inputCls} flex-1`}>
        {['00','15','30','45'].map(v => <option key={v}>{v}</option>)}
      </select>
      <div className="flex border border-gray-200 rounded-xl overflow-hidden shrink-0">
        {['AM','PM'].map(period => (
          <button key={period} type="button" onClick={() => onChange(fmt(h, m, period))}
            className={`px-3 py-2 text-xs font-bold transition-colors ${p===period ? 'bg-apex-blue-700 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
            {period}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Signature Pad ─────────────────────────────────────────────────────────
function SignaturePad({ value, onChange }) {
  const canvasRef  = useRef(null);
  const drawingRef = useRef(false);
  const [hasSig, setHasSig] = useState(false);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.strokeStyle = '#0D2B6B';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
  }, []);

  const getPos = (e, canvas) => {
    const rect   = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    const src    = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * scaleX, y: (src.clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    setHasSig(true);
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.lineTo(x, y); ctx.stroke();
  };
  const stop = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasSig(false);
    onChange(null);
  };

  return (
    <div className="relative">
      <div className={`border-2 rounded-xl overflow-hidden bg-white relative ${hasSig ? 'border-apex-blue-300' : 'border-dashed border-gray-300'}`}>
        <canvas ref={canvasRef} width={500} height={140} className="w-full cursor-crosshair touch-none block"
          onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={stop} />
        {!hasSig && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-gray-300 text-sm">✍️ Customer signs here</p>
          </div>
        )}
      </div>
      <div className="flex justify-between items-center mt-1.5">
        <p className="text-xs text-gray-400">Draw signature with mouse or finger</p>
        {hasSig && (
          <button type="button" onClick={clear}
            className="text-xs text-red-500 font-bold hover:text-red-700 border border-red-200 px-2 py-0.5 rounded-lg">
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

// ── Image Stages (camera / gallery / URL) ──────────────────────────────────
function ImageStages({ images = {}, bookingId, onRefresh }) {
  const [stage, setStage]         = useState('customer');
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl]     = useState(false);
  const [urlInput, setUrlInput]   = useState('');
  const [localImages, setLocalImages] = useState(images); // local optimistic state
  const [lightbox, setLightbox]   = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const cameraRef  = useRef(null);
  const galleryRef = useRef(null);

  // Sync when parent images change (initial load)
  useEffect(() => { setLocalImages(images); }, [JSON.stringify(images)]);

  const stages = [
    { id: 'customer', label: '📸 Customer', desc: 'Photos from customer' },
    { id: 'start',    label: '🚛 Job Start', desc: 'Driver photos before job' },
    { id: 'complete', label: '✅ Completed', desc: 'Photos after completion' },
  ];
  const stageImgs = localImages[stage] || [];

  const persist = async (updated) => {
    setLocalImages(updated); // instant local update
    await api.put(`/api/bookings/${bookingId}`, { images: updated });
    onRefresh(); // background sync
  };

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !bookingId) return;
    setUploading(true);
    try {
      const newImgs = await Promise.all(files.map(async f => ({
        id: Date.now().toString() + Math.random(),
        url: await resizeImage(f),
        added: new Date().toISOString().split('T')[0],
      })));
      const updated = { ...localImages, [stage]: [...(localImages[stage]||[]), ...newImgs] };
      await persist(updated);
    } catch { alert('Photo upload failed — try again.'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const addByUrl = async () => {
    if (!urlInput.trim() || !bookingId) return;
    const newImg  = { id: Date.now().toString(), url: urlInput.trim(), added: new Date().toISOString().split('T')[0] };
    const updated = { ...localImages, [stage]: [...(localImages[stage]||[]), newImg] };
    setUrlInput(''); setShowUrl(false);
    await persist(updated);
  };

  const delPhoto = async (imgId) => {
    const updated = { ...localImages, [stage]: (localImages[stage]||[]).filter(i => i.id !== imgId) };
    await persist(updated);
  };

  const copyToGallery = async (img) => {
    setCopyingId(img.id);
    try {
      await api.post('/api/gallery', { url: img.url, title: `Job Photo — ${stage}`, category: 'operations', published: false });
      alert('✅ Copied to Gallery (unpublished) — go to Gallery admin to publish it to the website');
    } catch { alert('Failed to copy to gallery.'); }
    finally { setCopyingId(null); }
  };

  return (
    <div>
      {/* Hidden file inputs — multiple allowed */}
      <input ref={cameraRef}  type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handleFile} />
      <input ref={galleryRef} type="file" accept="image/*"                       multiple className="hidden" onChange={handleFile} />

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-w-full max-h-[90vh] rounded-xl object-contain" onClick={e => e.stopPropagation()} />
          <button className="absolute top-4 right-4 bg-white/20 text-white p-2 rounded-full" onClick={() => setLightbox(null)}>
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Stage tabs */}
      <div className="flex gap-2 mb-3">
        {stages.map(s => {
          const cnt = (localImages[s.id] || []).length;
          return (
            <button key={s.id} onClick={() => setStage(s.id)}
              className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold border-2 transition-all text-center
                ${stage===s.id ? 'border-apex-blue-500 bg-apex-blue-50 text-apex-blue-700' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`}>
              {s.label}
              {cnt > 0 && <span className="ml-1 bg-apex-blue-500 text-white rounded-full px-1.5 text-[10px]">{cnt}</span>}
            </button>
          );
        })}
      </div>

      {/* Photo grid */}
      {stageImgs.length > 0 ? (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {stageImgs.map(img => (
            <div key={img.id} className="relative aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 group">
              <img src={img.url} alt="" className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => setLightbox(img.url)}
                onError={e => { e.target.style.display='none'; }} />
              {/* Hover actions */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                <button onClick={() => setLightbox(img.url)}
                  className="bg-white text-apex-blue-700 text-[10px] font-bold px-2 py-1 rounded-lg hover:bg-blue-50">
                  View
                </button>
                <button onClick={() => copyToGallery(img)} disabled={copyingId === img.id}
                  className="bg-apex-yellow-500 text-apex-blue-700 text-[10px] font-bold px-2 py-1 rounded-lg hover:bg-yellow-400 disabled:opacity-50">
                  {copyingId === img.id ? '...' : '📤 Gallery'}
                </button>
                <button onClick={() => delPhoto(img.id)}
                  className="bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-lg hover:bg-red-600">
                  Del
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-xl py-5 text-center text-gray-400 text-sm mb-3">
          No photos for this stage yet
        </div>
      )}

      {/* Upload buttons */}
      {uploading ? (
        <div className="flex items-center justify-center gap-2 py-3 text-apex-blue-600 text-sm font-semibold">
          <Loader className="w-4 h-4 animate-spin" /> Uploading...
        </div>
      ) : showUrl ? (
        <div className="flex gap-2">
          <input value={urlInput} onChange={e => setUrlInput(e.target.value)}
            placeholder="Paste image URL..." className={`${inputCls} flex-1`}
            onKeyDown={e => e.key==='Enter' && addByUrl()} />
          <button onClick={addByUrl} className="bg-apex-blue-700 text-white px-4 py-2 rounded-xl text-sm font-bold">Add</button>
          <button onClick={() => { setShowUrl(false); setUrlInput(''); }}
            className="border border-gray-200 px-3 py-2 rounded-xl text-sm text-gray-500">✕</button>
        </div>
      ) : (
        <div className="flex gap-2">
          <button onClick={() => cameraRef.current?.click()}
            className="flex-1 py-2.5 border-2 border-dashed border-apex-blue-200 rounded-xl text-xs font-bold text-apex-blue-600 hover:bg-apex-blue-50 transition-colors flex items-center justify-center gap-1.5">
            <Camera className="w-4 h-4" /> Take Photo
          </button>
          <button onClick={() => galleryRef.current?.click()}
            className="flex-1 py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-500 hover:border-apex-blue-300 hover:text-apex-blue-500 transition-colors flex items-center justify-center gap-1.5">
            <ImageIcon className="w-4 h-4" /> Gallery / Files
          </button>
          <button onClick={() => setShowUrl(true)} title="Add by URL"
            className="px-3 py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 hover:text-gray-600 hover:border-gray-300 transition-colors">
            <LinkIcon className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Invoice Creator ────────────────────────────────────────────────────────
function InvoiceCreator({ open, onClose, booking, onSave, companySettings }) {
  const isCash = ['Cash for Cars'].includes(booking?.service_type);
  const isFree = ['Free Car Removal'].includes(booking?.service_type);
  const invoiceMode = isCash ? 'cash_for_cars' : isFree ? 'free_removal' : 'towing';

  const [step, setStep]     = useState(1);
  const [saving, setSaving] = useState(false);
  const [sig, setSig]       = useState(null);
  const [form, setForm]     = useState({
    vehicle_make:'', vehicle_model:'', vehicle_year:'', vehicle_color:'',
    vehicle_rego:'', vehicle_vin:'', vehicle_transmission:'', vehicle_odometer:'',
    vehicle_condition:'', pickup:'', pickup_suburb:'', dropoff:'', dropoff_suburb:'',
    service_desc:'', base_rate:0, after_hours:0, access_fee:0, heavy_fee:0,
    other_fee:0, other_desc:'', payment_method:'', notes:'', due_date:'',
    // Cash for Cars — owner pays customer
    customer_bank_name:'', customer_bsb:'', customer_account:'', customer_payid:'',
    cash_payment_method:'cash', // how owner pays customer
  });

  useEffect(() => {
    if (booking) {
      const due = new Date(); due.setDate(due.getDate() + 14);
      setForm(f => ({
        ...f,
        vehicle_make: booking.vehicle_make || '',
        vehicle_model: booking.vehicle_model || '',
        vehicle_year: booking.vehicle_year || '',
        vehicle_color: booking.vehicle_color || '',
        vehicle_rego: booking.vehicle_rego || '',
        vehicle_condition: booking.vehicle_condition || '',
        pickup: booking.pickup_address || '',
        pickup_suburb: booking.pickup_suburb || '',
        dropoff: booking.dropoff_address || '',
        dropoff_suburb: booking.dropoff_suburb || '',
        service_desc: booking.service_type || '',
        // Pre-fill from booking charge — never default to 0 silently
        base_rate: booking.charge || 0,
        due_date: due.toISOString().split('T')[0],
      }));
      setStep(1); setSig(null);
    }
  }, [booking]);

  const total = [form.base_rate, form.after_hours, form.access_fee, form.heavy_fee, form.other_fee]
    .reduce((s, v) => s + (Number(v) || 0), 0);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const confirm = async () => {
    setSaving(true);
    const items = invoiceMode === 'free_removal'
      ? [{ desc: `Free Car Removal — ${form.pickup_suburb || form.pickup || 'Brisbane'}`, total: 0 }]
      : invoiceMode === 'cash_for_cars'
      ? [{ desc: `Vehicle Purchase — ${[form.vehicle_year, form.vehicle_make, form.vehicle_model].filter(Boolean).join(' ') || 'Vehicle'}${form.vehicle_rego ? ` (${form.vehicle_rego})` : ''}`, total: Number(form.base_rate) }]
      : [
          { desc: form.service_desc, total: Number(form.base_rate) },
          ...(form.after_hours > 0 ? [{ desc:'After Hours Surcharge', total: Number(form.after_hours) }] : []),
          ...(form.access_fee > 0  ? [{ desc:'Access Fee',            total: Number(form.access_fee)  }] : []),
          ...(form.heavy_fee > 0   ? [{ desc:'Heavy Vehicle Surcharge',total: Number(form.heavy_fee)  }] : []),
          ...(form.other_fee > 0   ? [{ desc: form.other_desc||'Other',total: Number(form.other_fee)  }] : []),
        ];
    try {
      const res = await api.post('/api/invoices', {
        booking_id:     booking?.id,
        client_name:    booking?.customer_name,
        client_email:   booking?.customer_email,
        invoice_type:   invoiceMode === 'towing' ? 'towing' : 'car_purchase',
        invoice_mode:   invoiceMode,
        items,
        amount:         invoiceMode === 'free_removal' ? 0 : total,
        payment_method: invoiceMode === 'cash_for_cars' ? form.cash_payment_method : form.payment_method,
        due_date:       form.due_date,
        notes:          form.notes,
        vehicle:        form,
        signature:      sig,
        // Cash for Cars — customer payment details
        customer_bank_name:    form.customer_bank_name,
        customer_bsb:          form.customer_bsb,
        customer_account:      form.customer_account,
        customer_payid:        form.customer_payid,
      });
      setStep(4);
      onSave(res.data);
    } catch { alert('Failed to create invoice — check your connection'); }
    finally { setSaving(false); }
  };

  const printInv = () => {
    const co  = companySettings || {};
    const gst = invoiceMode === 'free_removal' ? 0 : Math.round(total / 11);
    const win = window.open('', '_blank', 'width=900,height=750');

    const headerColor = invoiceMode === 'cash_for_cars' ? '#15803d' : invoiceMode === 'free_removal' ? '#0369a1' : '#0D2B6B';
    const titleText   = invoiceMode === 'cash_for_cars' ? 'CAR PURCHASE RECORD' : invoiceMode === 'free_removal' ? 'SERVICE COMPLETION RECORD' : 'TAX INVOICE';
    const items = invoiceMode === 'free_removal'
      ? [{ desc: `Free Car Removal — ${form.pickup_suburb || form.pickup || 'Brisbane'}`, total: 0 }]
      : invoiceMode === 'cash_for_cars'
      ? [{ desc: `Vehicle Purchase — ${[form.vehicle_year, form.vehicle_make, form.vehicle_model].filter(Boolean).join(' ') || 'Vehicle'}${form.vehicle_rego ? ` (${form.vehicle_rego})` : ''}`, total: Number(form.base_rate) }]
      : [
          { desc: form.service_desc, total: Number(form.base_rate) },
          ...(form.after_hours > 0 ? [{ desc:'After Hours Surcharge', total: Number(form.after_hours) }] : []),
          ...(form.access_fee > 0  ? [{ desc:'Access Fee',            total: Number(form.access_fee)  }] : []),
          ...(form.heavy_fee > 0   ? [{ desc:'Heavy Vehicle Surcharge',total: Number(form.heavy_fee)  }] : []),
          ...(form.other_fee > 0   ? [{ desc: form.other_desc||'Other',total: Number(form.other_fee)  }] : []),
        ];

    win.document.write(`<!DOCTYPE html><html><head><title>Invoice</title>
    <style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;padding:40px;color:#1a1a2e}
    .header{display:flex;justify-content:space-between;border-bottom:3px solid ${headerColor};padding-bottom:20px;margin-bottom:24px}
    table{width:100%;border-collapse:collapse;margin:16px 0}
    th{background:#f8f9fc;text-align:left;padding:10px;font-size:11px;color:#888;text-transform:uppercase}
    td{padding:10px;border-bottom:1px solid #eee;font-size:13px}
    .total-row{background:${headerColor};color:#fff;font-size:16px;font-weight:900}
    .pay-box{margin-top:20px;padding:14px;border-radius:8px;font-size:12px}
    .footer{margin-top:32px;padding-top:16px;border-top:1px solid #eee;text-align:center;font-size:11px;color:#888}
    @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}</style>
    </head><body>
    <div class="header">
      <div>
        ${co.logo_url ? `<img src="${co.logo_url}" style="height:50px;object-fit:contain;margin-bottom:8px;display:block" />` : ''}
        <div style="font-size:22px;font-weight:900;color:${headerColor}">${co.company_name || 'APEX TOWING SOLUTIONS'}</div>
        <div style="font-size:10px;letter-spacing:2px;color:#888;text-transform:uppercase">& Equipment Transport</div>
        <div style="margin-top:8px;font-size:12px;color:#555">${co.company_phone||''} · ${co.company_email||''}</div>
        ${co.company_abn ? `<div style="font-size:11px;color:#888">ABN: ${co.company_abn}</div>` : ''}
      </div>
      <div style="text-align:right">
        <div style="font-size:18px;font-weight:900;color:${headerColor}">${titleText}</div>
        <div style="font-size:12px;color:#888;margin-top:4px">Date: ${new Date().toLocaleDateString('en-AU')}</div>
        ${invoiceMode==='towing' ? `<div style="font-size:12px;color:#888">Due: ${form.due_date || 'On receipt'}</div>` : ''}
      </div>
    </div>

    ${invoiceMode === 'cash_for_cars' ? `
    <div style="background:#f0fdf4;border:2px solid #86efac;border-radius:8px;padding:16px;margin-bottom:20px">
      <div style="font-weight:900;color:#15803d;font-size:14px;margin-bottom:4px">🚗 Vehicle Purchase Agreement</div>
      <div style="font-size:12px;color:#166534">${co.company_name||'Apex Towing Solutions'} is purchasing the vehicle below from <strong>${booking?.customer_name}</strong>.</div>
    </div>` : ''}

    ${invoiceMode === 'free_removal' ? `
    <div style="background:#f0f9ff;border:2px solid #7dd3fc;border-radius:8px;padding:16px;margin-bottom:20px">
      <div style="font-weight:900;color:#0369a1;font-size:14px;margin-bottom:4px">✅ Free Car Removal — No Charge</div>
      <div style="font-size:12px;color:#0c4a6e">This vehicle has been removed at no cost to the customer. No payment is required.</div>
    </div>` : ''}

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:20px">
      <div style="background:#f8f9fc;padding:14px;border-radius:8px">
        <div style="font-size:10px;color:#888;font-weight:700;margin-bottom:4px;text-transform:uppercase">
          ${invoiceMode==='cash_for_cars' ? 'Seller (Customer)' : 'Customer'}
        </div>
        <div style="font-weight:700;font-size:16px">${booking?.customer_name}</div>
        <div style="font-size:13px;color:#555;margin-top:2px">${booking?.customer_phone||''}</div>
      </div>
      <div style="background:#f8f9fc;padding:14px;border-radius:8px">
        <div style="font-size:10px;color:#888;font-weight:700;margin-bottom:4px;text-transform:uppercase">Vehicle</div>
        <div style="font-weight:700">${[form.vehicle_year, form.vehicle_make, form.vehicle_model].filter(Boolean).join(' ')||'—'}</div>
        ${form.vehicle_rego ? `<div style="font-size:12px;color:#555">Rego: ${form.vehicle_rego}</div>` : ''}
        ${form.vehicle_condition ? `<div style="font-size:12px;color:#555">Condition: ${form.vehicle_condition}</div>` : ''}
        ${form.pickup_suburb ? `<div style="font-size:12px;color:#555">Location: ${form.pickup_suburb}</div>` : ''}
      </div>
    </div>

    <table>
      <thead><tr>
        <th>Description</th>
        <th style="text-align:right">${invoiceMode==='cash_for_cars' ? 'Amount Paid to Seller' : 'Amount'}</th>
      </tr></thead>
      <tbody>
        ${items.map(i => `<tr><td>${i.desc}</td><td style="text-align:right;font-weight:600">${i.total===0?'FREE':'$'+i.total.toLocaleString()}</td></tr>`).join('')}
      </tbody>
      <tfoot>
        <tr class="total-row">
          <td style="padding:12px;border-radius:0 0 0 8px">
            ${invoiceMode==='cash_for_cars' ? 'TOTAL PAID TO SELLER' : invoiceMode==='free_removal' ? 'TOTAL CHARGE' : 'TOTAL (incl. GST)'}
          </td>
          <td style="padding:12px;text-align:right;font-size:18px;border-radius:0 0 8px 0">
            ${invoiceMode==='free_removal' ? 'FREE' : '$'+total.toLocaleString()}
          </td>
        </tr>
      </tfoot>
    </table>

    ${invoiceMode==='towing' && gst > 0 ? `<div style="font-size:12px;color:#888;margin-bottom:16px">GST included: $${gst.toLocaleString()} | Ex-GST: $${(total-gst).toLocaleString()}</div>` : ''}

    ${invoiceMode==='cash_for_cars' ? `
    <div class="pay-box" style="background:#f0fdf4;border:1px solid #86efac">
      <strong style="color:#15803d">Payment to Seller</strong><br/>
      <div style="margin-top:6px;font-size:12px">
        ${form.cash_payment_method==='cash' ? '💵 Cash payment made in person at time of collection.' : ''}
        ${form.cash_payment_method==='bank_transfer' && form.customer_bsb ? `🏦 Bank Transfer to:<br/>Bank: ${form.customer_bank_name||'—'} · BSB: ${form.customer_bsb} · Account: ${form.customer_account}` : ''}
        ${form.cash_payment_method==='payid' && form.customer_payid ? `📱 PayID: ${form.customer_payid}` : ''}
      </div>
    </div>` : ''}

    ${invoiceMode==='towing' && (co.bsb || co.payid) ? `
    <div class="pay-box" style="background:#f8f9fc;border:1px solid #e2e8f0">
      <strong>Payment Details</strong><br/>
      ${co.bsb ? `BSB: ${co.bsb} · Account: ${co.account_number} · Name: ${co.account_name}<br/>` : ''}
      ${co.payid ? `PayID: ${co.payid}` : ''}
      ${co.payment_terms ? `<br/>${co.payment_terms}` : ''}
    </div>` : ''}

    ${sig ? `
    <div style="margin-top:24px;padding-top:16px;border-top:1px solid #eee">
      <div style="font-size:10px;color:#888;font-weight:700;text-transform:uppercase;margin-bottom:8px">
        ${invoiceMode==='cash_for_cars' ? 'Seller Signature (confirms sale)' : 'Customer Signature'}
      </div>
      <img src="${sig}" style="height:70px;border-bottom:2px solid ${headerColor};padding-bottom:4px;display:block" />
      ${invoiceMode!=='cash_for_cars' ? `<div style="font-size:11px;color:#888;margin-top:4px">Payment method: ${form.payment_method}</div>` : ''}
    </div>` : ''}

    <div class="footer">${co.invoice_notes || 'Thank you for choosing Apex Towing Solutions!'}</div>
    </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  if (!open) return null;

  const modeColor  = invoiceMode==='cash_for_cars' ? '#15803d' : invoiceMode==='free_removal' ? '#0369a1' : '#0D2B6B';
  const modeTitle  = invoiceMode==='cash_for_cars' ? 'Car Purchase Record' : invoiceMode==='free_removal' ? 'Service Receipt' : 'Towing Invoice';
  const steps      = invoiceMode==='towing'
    ? ['Job Details','Charges','Payment & Sign','Invoice']
    : invoiceMode==='cash_for_cars'
    ? ['Vehicle Details','Purchase Amount','Payment to Seller','Record']
    : ['Job Details','Confirm','Record'];

  const totalSteps = steps.length;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target===e.currentTarget && step < totalSteps) onClose(); }}>
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[92vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="rounded-t-2xl px-6 py-5 shrink-0" style={{ backgroundColor: modeColor }}>
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-white/60 text-xs font-bold uppercase tracking-wider">{modeTitle}</p>
              <p className="text-white font-black text-lg mt-0.5">{booking?.customer_name}</p>
              <p className="text-white/50 text-xs mt-0.5">{booking?.service_type}</p>
            </div>
            {step < totalSteps && <button onClick={onClose} className="bg-white/15 text-white p-2 rounded-lg"><X className="w-4 h-4" /></button>}
          </div>
          {/* Step indicators */}
          <div className="flex items-center">
            {steps.map((s, i) => {
              const done = i+1 < step, act = i+1 === step;
              return (
                <React.Fragment key={s}>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black
                      ${done?'bg-green-400 text-white':act?'bg-yellow-400 text-gray-900':'bg-white/20 text-white/50'}`}>
                      {done?'✓':i+1}
                    </div>
                    <span className={`text-xs whitespace-nowrap hidden sm:block ${act?'text-white font-bold':'text-white/40'}`}>{s}</span>
                  </div>
                  {i < totalSteps-1 && <div className="flex-1 h-px bg-white/20 mx-2" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6">

          {/* ── TOWING: Step 1 — Job & Vehicle ── */}
          {invoiceMode==='towing' && step===1 && (
            <div className="space-y-4">
              <div className="bg-apex-blue-50 rounded-xl p-4">
                <p className="text-xs font-bold text-apex-blue-700 uppercase tracking-wide mb-3">Vehicle</p>
                <div className="grid grid-cols-2 gap-3">
                  {[['Make','vehicle_make','Toyota'],['Model','vehicle_model','Corolla'],['Year','vehicle_year','2020'],
                    ['Color','vehicle_color','White'],['Rego','vehicle_rego','ABC 123'],['VIN','vehicle_vin','Optional'],
                    ['Transmission','vehicle_transmission','Auto'],['Odometer','vehicle_odometer','80,000 km']].map(([l,k,ph]) => (
                    <Field key={k} label={l}><input value={form[k]} onChange={set(k)} placeholder={ph} className={inputCls} /></Field>
                  ))}
                  <Field label="Condition">
                    <select value={form.vehicle_condition} onChange={set('vehicle_condition')} className={selectCls}>
                      <option value="">Select</option>
                      <option value="running">Running / Driveable</option>
                      <option value="not_running">Not Running</option>
                      <option value="accident_damaged">Accident Damaged</option>
                    </select>
                  </Field>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Job Details</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Pickup Address"><input value={form.pickup} onChange={set('pickup')} className={inputCls} /></Field>
                  <Field label="Pickup Suburb"><input value={form.pickup_suburb} onChange={set('pickup_suburb')} className={inputCls} /></Field>
                  <Field label="Drop-off Address"><input value={form.dropoff} onChange={set('dropoff')} className={inputCls} /></Field>
                  <Field label="Drop-off Suburb"><input value={form.dropoff_suburb} onChange={set('dropoff_suburb')} className={inputCls} /></Field>
                </div>
                <div className="mt-3">
                  <Field label="Service Description">
                    <input value={form.service_desc} onChange={set('service_desc')} className={inputCls} placeholder="e.g. Emergency Towing — Valley to Chermside" />
                  </Field>
                </div>
              </div>
              <button onClick={() => setStep(2)} className="w-full py-3 rounded-xl font-bold text-white hover:opacity-90" style={{ backgroundColor: modeColor }}>
                Next: Charges →
              </button>
            </div>
          )}

          {/* ── TOWING: Step 2 — Charges ── */}
          {invoiceMode==='towing' && step===2 && (
            <div className="space-y-4">
              {/* Charge warning if still 0 */}
              {Number(form.base_rate) === 0 && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl px-4 py-3 text-sm text-amber-800 font-bold">
                  ⚠️ Base rate is $0 — enter the amount charged for this job
                </div>
              )}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Charge Breakdown</p>
                <div className="grid grid-cols-2 gap-3">
                  {[['Base Rate ($) *','base_rate'],['After Hours ($)','after_hours'],
                    ['Access Fee ($)','access_fee'],['Heavy Vehicle ($)','heavy_fee'],
                    ['Other ($)','other_fee'],['Other Description','other_desc']].map(([l,k]) => (
                    <Field key={k} label={l}>
                      <input type={k==='other_desc'?'text':'number'} value={form[k]} onChange={set(k)} className={inputCls}
                        style={k==='base_rate' && Number(form[k])===0 ? { borderColor:'#f59e0b' } : {}} />
                    </Field>
                  ))}
                </div>
              </div>
              <div className="rounded-xl p-5 flex justify-between items-center" style={{ backgroundColor: modeColor }}>
                <div>
                  <p className="text-white/60 text-xs font-bold uppercase">Total (incl. GST)</p>
                  <p className="text-white/50 text-xs">GST: ${Math.round(total/11).toLocaleString()}</p>
                </div>
                <p className="text-yellow-400 text-4xl font-black">${total.toLocaleString()}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Due Date"><input type="date" value={form.due_date} onChange={set('due_date')} className={inputCls} /></Field>
                <Field label="Notes"><input value={form.notes} onChange={set('notes')} placeholder="Optional" className={inputCls} /></Field>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="flex-1 border-2 border-gray-200 py-3 rounded-xl font-bold text-gray-500 hover:bg-gray-50">← Back</button>
                <button onClick={() => setStep(3)} disabled={total === 0}
                  className="flex-grow py-3 rounded-xl font-bold text-white disabled:opacity-50 hover:opacity-90" style={{ backgroundColor: modeColor }}>
                  Next: Payment →
                </button>
              </div>
            </div>
          )}

          {/* ── TOWING: Step 3 — Payment + Signature ── */}
          {invoiceMode==='towing' && step===3 && (
            <div className="space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase">How customer paid</p>
              <div className="grid grid-cols-2 gap-3">
                {[['cash','💵 Cash'],['card','💳 Card / EFTPOS'],['payid','📱 PayID'],['bank_transfer','🏦 Bank Transfer']].map(([v,l]) => (
                  <button key={v} type="button" onClick={() => setForm(f => ({ ...f, payment_method: v }))}
                    className={`py-4 rounded-xl border-2 font-bold text-sm transition-all
                      ${form.payment_method===v ? 'border-apex-blue-500 bg-apex-blue-50 text-apex-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="bg-apex-blue-50 rounded-xl p-4 text-sm">
                <div className="flex justify-between mb-1"><span className="text-gray-500">Customer</span><span className="font-bold">{booking?.customer_name}</span></div>
                <div className="flex justify-between mb-1"><span className="text-gray-500">Service</span><span className="font-bold">{form.service_desc}</span></div>
                <div className="flex justify-between border-t border-apex-blue-100 pt-2 mt-2">
                  <span className="font-bold text-apex-blue-700">Total</span>
                  <span className="text-xl font-black text-apex-blue-700">${total.toLocaleString()}</span>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Customer Signature (optional)</p>
                <SignaturePad value={sig} onChange={setSig} />
                {sig && <p className="text-xs text-green-600 font-bold mt-1.5">✅ Signature captured</p>}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="flex-1 border-2 border-gray-200 py-3 rounded-xl font-bold text-gray-500">← Back</button>
                <button onClick={confirm} disabled={!form.payment_method || saving}
                  className="flex-grow py-3 rounded-xl font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: modeColor }}>
                  {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Generate Invoice
                </button>
              </div>
            </div>
          )}

          {/* ── CASH FOR CARS: Step 1 — Vehicle ── */}
          {invoiceMode==='cash_for_cars' && step===1 && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700">
                🚗 You are <strong>buying</strong> this vehicle from the customer. This record confirms the purchase and payment made to the seller.
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-bold text-gray-500 uppercase mb-3">Vehicle Being Purchased</p>
                <div className="grid grid-cols-2 gap-3">
                  {[['Make','vehicle_make','Toyota'],['Model','vehicle_model','Corolla'],
                    ['Year','vehicle_year','2010'],['Rego','vehicle_rego','ABC 123'],
                    ['Color','vehicle_color','Silver'],['VIN','vehicle_vin','Optional']].map(([l,k,ph]) => (
                    <Field key={k} label={l}><input value={form[k]} onChange={set(k)} placeholder={ph} className={inputCls} /></Field>
                  ))}
                  <Field label="Vehicle Condition">
                    <select value={form.vehicle_condition} onChange={set('vehicle_condition')} className={selectCls}>
                      <option value="">Select</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Poor">Poor</option>
                      <option value="Scrap">Scrap / Parts Only</option>
                    </select>
                  </Field>
                  <Field label="Location (Suburb)"><input value={form.pickup_suburb} onChange={set('pickup_suburb')} className={inputCls} /></Field>
                </div>
              </div>
              <button onClick={() => setStep(2)} className="w-full py-3 rounded-xl font-bold text-white" style={{ backgroundColor: modeColor }}>
                Next: Purchase Amount →
              </button>
            </div>
          )}

          {/* ── CASH FOR CARS: Step 2 — Purchase Amount ── */}
          {invoiceMode==='cash_for_cars' && step===2 && (
            <div className="space-y-4">
              {Number(form.base_rate) === 0 && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl px-4 py-3 text-sm text-amber-800 font-bold">
                  ⚠️ Amount is $0 — enter how much you're paying the customer
                </div>
              )}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-bold text-gray-500 uppercase mb-3">Amount to Pay Seller</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-black text-gray-300">$</span>
                  <input type="number" value={form.base_rate} onChange={set('base_rate')}
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl text-3xl font-black focus:outline-none focus:border-green-500"
                    placeholder="0" />
                </div>
                <p className="text-xs text-gray-400 mt-2">This is the cash/transfer amount you will pay to {booking?.customer_name}</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex justify-between items-center">
                <span className="text-green-700 font-bold">Total to Pay Customer</span>
                <span className="text-3xl font-black text-green-700">${Number(form.base_rate).toLocaleString()}</span>
              </div>
              <Field label="Notes (optional)">
                <input value={form.notes} onChange={set('notes')} placeholder="e.g. Paid on collection" className={inputCls} />
              </Field>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="flex-1 border-2 border-gray-200 py-3 rounded-xl font-bold text-gray-500">← Back</button>
                <button onClick={() => setStep(3)} disabled={Number(form.base_rate) === 0}
                  className="flex-grow py-3 rounded-xl font-bold text-white disabled:opacity-50" style={{ backgroundColor: modeColor }}>
                  Next: Payment Details →
                </button>
              </div>
            </div>
          )}

          {/* ── CASH FOR CARS: Step 3 — How owner pays customer ── */}
          {invoiceMode==='cash_for_cars' && step===3 && (
            <div className="space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase">How are you paying the seller?</p>
              <div className="grid grid-cols-3 gap-2">
                {[['cash','💵 Cash in Hand'],['bank_transfer','🏦 Bank Transfer'],['payid','📱 PayID']].map(([v,l]) => (
                  <button key={v} type="button" onClick={() => setForm(f => ({ ...f, cash_payment_method: v }))}
                    className={`py-3 px-2 rounded-xl border-2 font-bold text-xs transition-all text-center
                      ${form.cash_payment_method===v ? 'bg-green-50 text-green-700 border-green-500' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                    {l}
                  </button>
                ))}
              </div>

              {form.cash_payment_method === 'cash' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-700">
                  💵 Cash payment — hand over <strong>${Number(form.base_rate).toLocaleString()}</strong> to <strong>{booking?.customer_name}</strong> at time of vehicle collection.
                </div>
              )}

              {form.cash_payment_method === 'bank_transfer' && (
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <p className="text-xs font-bold text-gray-500 uppercase">Customer's Bank Details (to transfer to)</p>
                  <Field label="Bank Name"><input value={form.customer_bank_name} onChange={set('customer_bank_name')} className={inputCls} placeholder="e.g. Commonwealth Bank" /></Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="BSB"><input value={form.customer_bsb} onChange={set('customer_bsb')} className={inputCls} placeholder="062-xxx" /></Field>
                    <Field label="Account Number"><input value={form.customer_account} onChange={set('customer_account')} className={inputCls} placeholder="12345678" /></Field>
                  </div>
                </div>
              )}

              {form.cash_payment_method === 'payid' && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-2">Customer's PayID</p>
                  <input value={form.customer_payid} onChange={set('customer_payid')} className={inputCls}
                    placeholder="Customer's phone number or email as PayID" />
                </div>
              )}

              <div>
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Seller Signature (confirms they received payment)</p>
                <SignaturePad value={sig} onChange={setSig} />
                {sig && <p className="text-xs text-green-600 font-bold mt-1.5">✅ Signature captured</p>}
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="flex-1 border-2 border-gray-200 py-3 rounded-xl font-bold text-gray-500">← Back</button>
                <button onClick={confirm} disabled={saving}
                  className="flex-grow py-3 rounded-xl font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: modeColor }}>
                  {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Record Purchase
                </button>
              </div>
            </div>
          )}

          {/* ── FREE REMOVAL: Step 1 — Confirm ── */}
          {invoiceMode==='free_removal' && step===1 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
                This is a <strong>free service</strong> — no payment is required. This receipt confirms the vehicle was removed at no charge.
              </div>
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase">Vehicle Details</p>
                <div className="grid grid-cols-2 gap-3">
                  {[['Make','vehicle_make'],['Model','vehicle_model'],['Year','vehicle_year'],['Rego','vehicle_rego'],['Color','vehicle_color']].map(([l,k]) => (
                    <Field key={k} label={l}><input value={form[k]} onChange={set(k)} className={inputCls} /></Field>
                  ))}
                  <Field label="Location">
                    <input value={form.pickup_suburb} onChange={set('pickup_suburb')} className={inputCls} placeholder="Suburb" />
                  </Field>
                </div>
              </div>
              <Field label="Notes">
                <input value={form.notes} onChange={set('notes')} placeholder="Optional notes" className={inputCls} />
              </Field>
              <button onClick={() => setStep(2)} className="w-full py-3 rounded-xl font-bold text-white" style={{ backgroundColor: modeColor }}>
                Next: Confirm & Sign →
              </button>
            </div>
          )}

          {/* ── FREE REMOVAL: Step 2 — Sign ── */}
          {invoiceMode==='free_removal' && step===2 && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-xl p-4 text-sm">
                <div className="flex justify-between mb-2"><span className="text-gray-500">Customer</span><span className="font-bold">{booking?.customer_name}</span></div>
                <div className="flex justify-between mb-2"><span className="text-gray-500">Service</span><span className="font-bold">Free Car Removal</span></div>
                <div className="flex justify-between mb-2"><span className="text-gray-500">Location</span><span className="font-bold">{form.pickup_suburb || '—'}</span></div>
                <div className="flex justify-between border-t border-blue-200 pt-2 mt-2">
                  <span className="font-bold text-blue-700">Amount Charged</span>
                  <span className="text-xl font-black text-blue-700">FREE</span>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Customer Signature (confirms removal)</p>
                <SignaturePad value={sig} onChange={setSig} />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="flex-1 border-2 border-gray-200 py-3 rounded-xl font-bold text-gray-500">← Back</button>
                <button onClick={confirm} disabled={saving}
                  className="flex-grow py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2" style={{ backgroundColor: modeColor }}>
                  {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Generate Receipt
                </button>
              </div>
            </div>
          )}

          {/* ── DONE (all modes) ── */}
          {step===totalSteps && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#dcfce7' }}>
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-black text-apex-blue-700 mb-2">
                {invoiceMode==='cash_for_cars' ? 'Purchase Recorded!' : invoiceMode==='free_removal' ? 'Receipt Created!' : 'Invoice Created!'}
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                {invoiceMode==='cash_for_cars'
                  ? `Purchase of ${[form.vehicle_year,form.vehicle_make,form.vehicle_model].filter(Boolean).join(' ')} recorded · $${Number(form.base_rate).toLocaleString()} paid to seller`
                  : invoiceMode==='free_removal'
                  ? 'Free car removal receipt generated'
                  : `Payment: ${form.payment_method}${sig?' · Signature captured':''}`}
              </p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button onClick={printInv} className="flex flex-col items-center gap-2 py-4 border-2 border-gray-200 rounded-xl hover:border-apex-blue-300 hover:bg-apex-blue-50 transition-all">
                  <Printer className="w-6 h-6 text-apex-blue-700" />
                  <span className="text-sm font-semibold text-gray-700">Print</span>
                </button>
                <button onClick={async () => {
                  if (!booking?.customer_email) { alert('No email on this booking.'); return; }
                  try {
                    await api.post(`/api/invoices/${booking.invoice_id}/send-email`);
                    alert(`Sent to ${booking.customer_email}`);
                  } catch(e) { alert(e.response?.data?.detail || 'Email failed — check SMTP in Settings.'); }
                }} className="flex flex-col items-center gap-2 py-4 border-2 border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-all">
                  <FileText className="w-6 h-6 text-green-600" />
                  <span className="text-sm font-semibold text-gray-700">Email</span>
                </button>
              </div>
              <button onClick={() => { setStep(1); onClose(); }} className="w-full bg-apex-blue-700 text-white py-3 rounded-xl font-bold hover:bg-apex-blue-600">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Bookings Component ────────────────────────────────────────────────
const STATUS_OPTS = ['pending','confirmed','in_progress','completed','invoiced','cancelled'];

export default function Bookings() {
  const [bookings, setBookings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [dateTab, setDateTab]     = useState('today');
  const [statusF, setStatusF]     = useState('');
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showInvoice, setShowInvoice]         = useState(false);
  const [viewingExistingInv, setViewingExistingInv] = useState(null);
  const [carPurchasePrompt, setCarPurchasePrompt] = useState(null); // booking to record as car purchase
  const [cpForm, setCpForm] = useState({ paid:'', condition:'', notes:'' });
  const [createStep, setCreateStep] = useState(1);
  const [tabCounts, setTabCounts] = useState({});
  const [toast, setToast]         = useState('');
  const [companySettings, setCompanySettings] = useState({});
  const [form, setForm] = useState({
    customer_name:'', customer_phone:'', customer_email:'', service_type:'',
    pickup_address:'', pickup_suburb:'', dropoff_address:'', dropoff_suburb:'',
    scheduled_date:'', scheduled_time:'', charge:'', payment_method:'',
    vehicle_make:'', vehicle_model:'', vehicle_year:'', vehicle_color:'',
    vehicle_rego:'', vehicle_condition:'', notes:'', vehicle_photo:'',
  });

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const markCompleted = (booking) => {
    updateBooking(booking.id, { status: 'completed' });
    if (['Cash for Cars', 'Free Car Removal'].includes(booking.service_type)) {
      setCpForm({
        paid:      String(booking.charge || ''),
        condition: booking.vehicle_condition || '',
        notes:     '',
      });
      setCarPurchasePrompt(booking);
    }
  };

  const saveCarPurchase = async () => {
    if (!carPurchasePrompt) return;
    try {
      await api.post('/api/car-purchases', {
        customer_name:  carPurchasePrompt.customer_name,
        customer_phone: carPurchasePrompt.customer_phone || '',
        vehicle: [carPurchasePrompt.vehicle_year, carPurchasePrompt.vehicle_make, carPurchasePrompt.vehicle_model].filter(Boolean).join(' ') || 'Unknown Vehicle',
        rego:      carPurchasePrompt.vehicle_rego || '',
        condition: cpForm.condition || carPurchasePrompt.vehicle_condition || 'Fair',
        paid:      Number(cpForm.paid) || 0,
        date:      new Date().toISOString().split('T')[0],
        notes:     cpForm.notes || `From booking ${carPurchasePrompt.id?.slice(-6)}`,
        photos:    Object.values(carPurchasePrompt.images || {}).flat().map(i => i.url).filter(Boolean),
      });
      showToast('✅ Car purchase recorded!');
    } catch { showToast('Could not record car purchase — add it manually in Car Purchases.'); }
    setCarPurchasePrompt(null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateTab) params.append('date_filter', dateTab);
      if (statusF) params.append('status', statusF);
      if (search)  params.append('search', search);
      const res = await api.get(`/api/bookings?${params}`);
      setBookings(res.data);
    } catch (e) {} finally { setLoading(false); }
  }, [dateTab, statusF, search]);

  const loadTabCounts = useCallback(async () => {
    const tabs = ['yesterday','today','tomorrow','this_week','this_month',''];
    const results = await Promise.all(tabs.map(async t => {
      try {
        const p = new URLSearchParams();
        if (t) p.append('date_filter', t);
        const res = await api.get(`/api/bookings?${p}`);
        return [t, res.data.length];
      } catch { return [t, 0]; }
    }));
    setTabCounts(Object.fromEntries(results));
  }, []);

  useEffect(() => {
    api.get('/api/settings/general').then(r => setCompanySettings(r.data || {})).catch(() => {});
    loadTabCounts();
  }, [loadTabCounts]);

  useEffect(() => { load(); }, [load]);

  const updateBooking = async (id, data) => {
    await api.put(`/api/bookings/${id}`, data);
    load();
    if (selected?.id === id) setSelected(s => ({ ...s, ...data }));
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    await api.delete(`/api/bookings/${id}`);
    load(); loadTabCounts(); setSelected(null);
  };

  const resetForm = {
    customer_name:'', customer_phone:'', customer_email:'', service_type:'',
    pickup_address:'', pickup_suburb:'', dropoff_address:'', dropoff_suburb:'',
    scheduled_date:'', scheduled_time:'', charge:'', payment_method:'',
    vehicle_make:'', vehicle_model:'', vehicle_year:'', vehicle_color:'',
    vehicle_rego:'', vehicle_condition:'', notes:'', vehicle_photo:'',
  };

  const createBooking = async () => {
    try {
      await api.post('/api/bookings', { ...form, charge: Number(form.charge)||0, booking_type:'admin' });
      setShowCreate(false); setCreateStep(1); setForm(resetForm);
      setDateTab(''); // switch to All Time so booking is visible
      showToast('✅ Booking created!');
      load(); loadTabCounts();
    } catch { alert('Failed to create booking — check all required fields.'); }
  };

  const sf = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const isLocked = b => b.status === 'invoiced' || (b.status === 'completed' && b.payment_status === 'paid');

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-apex-blue-700 text-white px-5 py-3 rounded-xl font-semibold text-sm shadow-xl z-50 flex items-center gap-2">
          <Check className="w-4 h-4" />{toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-black text-apex-blue-700">Bookings</h1>
          <p className="text-sm text-gray-500 mt-0.5">{bookings.length} shown</p>
        </div>
        <button onClick={() => { setShowCreate(true); setCreateStep(1); }}
          className="flex items-center gap-2 bg-apex-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-apex-blue-600 shadow-md">
          <Plus className="w-4 h-4" /> New Booking
        </button>
      </div>

      {/* Date tabs with counts */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {DATE_TABS.map(({ id, label }) => {
          const cnt = tabCounts[id] ?? 0;
          return (
            <button key={id} onClick={() => setDateTab(id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border-2 whitespace-nowrap transition-all shrink-0
                ${dateTab===id ? 'bg-apex-blue-700 border-apex-blue-700 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
              {label}
              {cnt > 0 && (
                <span className={`text-[11px] font-black px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-none
                  ${dateTab===id ? 'bg-white/25 text-white' : 'bg-apex-blue-100 text-apex-blue-700'}`}>
                  {cnt}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 flex-1 min-w-48">
          <Search className="w-4 h-4 text-gray-300 shrink-0" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, phone, rego..." className="outline-none text-sm flex-1" />
        </div>
        <select value={statusF} onChange={e => setStatusF(e.target.value)}
          className="border border-gray-200 bg-white rounded-xl px-3 py-2.5 text-sm text-gray-700">
          <option value="">All Statuses</option>
          {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader className="w-8 h-8 text-apex-blue-500 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Ref','Customer','Service','Vehicle','Date & Time','Status','Payment','Charge','Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bookings.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-16 text-gray-400">
                    <Calendar className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                    <p className="font-medium">No bookings for this period</p>
                    <p className="text-xs mt-1">Try "All Time" to see all bookings</p>
                  </td></tr>
                )}
                {bookings.map(b => {
                  const locked = isLocked(b);
                  return (
                    <tr key={b.id} onClick={() => setSelected(b)}
                      className={`cursor-pointer transition-colors hover:bg-blue-50/50 ${locked ? 'bg-purple-50/20' : ''}`}>
                      <td className="px-4 py-3.5 text-xs font-mono text-gray-400">{b.id?.slice(-6)}</td>
                      <td className="px-4 py-3.5">
                        <p className="font-bold text-gray-900">{b.customer_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{b.customer_phone}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="font-medium text-gray-800 text-sm">{b.service_type}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        {b.vehicle_make
                          ? <><p className="font-medium text-gray-800 text-sm">{b.vehicle_year} {b.vehicle_make} {b.vehicle_model}</p>
                              <p className="text-xs text-gray-400 mt-0.5">{b.vehicle_rego}</p></>
                          : <span className="text-gray-300 text-sm">—</span>
                        }
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {b.scheduled_date
                          ? <><p className="font-semibold text-gray-800 text-sm">{b.scheduled_date}</p>
                              <p className="text-xs text-gray-400 mt-0.5">{b.scheduled_time}</p></>
                          : <span className="text-gray-400 text-sm">TBC</span>
                        }
                      </td>
                      <td className="px-4 py-3.5"><Badge label={b.status} /></td>
                      <td className="px-4 py-3.5">
                        <Badge label={b.payment_status} />
                        {b.payment_method && <p className="text-[10px] text-gray-400 mt-0.5 capitalize">{b.payment_method.replace('_',' ')}</p>}
                      </td>
                      <td className="px-4 py-3.5 font-black text-apex-blue-700 text-base whitespace-nowrap">
                        {b.service_type==='Free Car Removal'
                          ? <span className="text-blue-600 font-bold text-sm">FREE</span>
                          : b.service_type==='Cash for Cars'
                          ? <span className="text-green-600 font-bold text-sm">Pay Cust.</span>
                          : b.charge
                          ? `$${Number(b.charge).toLocaleString()}`
                          : <span className="text-amber-500 font-bold text-xs">⚠️ Not set</span>}
                      </td>
                      <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1.5">
                          {locked && <span className="text-sm" title="Invoiced">🔒</span>}
                          <button onClick={() => setSelected(b)} className="p-1.5 rounded-lg border border-gray-200 hover:bg-blue-50 text-apex-blue-500"><Eye className="w-3.5 h-3.5" /></button>
                          {!locked && (
                            <button onClick={() => { setSelected(b); setShowInvoice(true); }}
                              className="p-1.5 rounded-lg border border-green-200 hover:bg-green-50 text-green-600">
                              <FileText className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {!locked && (
                            <button onClick={() => deleteBooking(b.id)} className="p-1.5 rounded-lg border border-red-200 hover:bg-red-50 text-red-500">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Detail Modal ── */}
      <Modal open={!!selected && !showInvoice} onClose={() => setSelected(null)} title={`Booking ${selected?.id?.slice(-6)}`} maxWidth="max-w-2xl">
        {selected && (() => {
          const locked = isLocked(selected);
          return (
            <div className="space-y-4">
              {locked && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex gap-3 items-start">
                  <span className="text-xl">🔒</span>
                  <div><p className="font-bold text-purple-800 text-sm">Invoiced & paid — view only</p>
                  <p className="text-xs text-purple-600 mt-0.5">This booking has been invoiced and payment recorded.</p></div>
                </div>
              )}
              {/* Info grid */}
              <div className="grid grid-cols-3 gap-3 bg-gray-50 rounded-xl p-4">
                {[
                  ['Customer', selected.customer_name],
                  ['Phone',    selected.customer_phone],
                  ['Email',    selected.customer_email || '—'],
                  ['Service',  selected.service_type],
                  ['Pickup',   `${selected.pickup_address} ${selected.pickup_suburb}`],
                  ['Drop-off', selected.dropoff_address || 'N/A'],
                  ['Scheduled',selected.scheduled_date ? `${selected.scheduled_date} ${selected.scheduled_time}` : 'TBC'],
                  ['Charge', selected.charge
                    ? `$${Number(selected.charge).toLocaleString()}`
                    : <span className="text-amber-600 font-bold text-sm">⚠️ Not set</span>],
                  ['Type',     selected.booking_type],
                ].map(([l,v]) => (
                  <div key={l}>
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-0.5">{l}</p>
                    <p className="text-sm font-semibold text-gray-800">{v}</p>
                  </div>
                ))}
              </div>

              {/* Vehicle */}
              {selected.vehicle_make && (
                <div className="bg-apex-blue-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-apex-blue-700 uppercase tracking-wide mb-3">Vehicle</p>
                  <div className="grid grid-cols-4 gap-3 text-xs">
                    {[['Make',selected.vehicle_make],['Model',selected.vehicle_model],
                      ['Year',selected.vehicle_year],['Color',selected.vehicle_color],
                      ['Rego',selected.vehicle_rego],['VIN',selected.vehicle_vin||'—'],
                      ['Trans.',selected.vehicle_transmission||'—'],['Odo',selected.vehicle_odometer||'—'],
                    ].map(([l,v]) => (
                      <div key={l}>
                        <p className="text-gray-400 uppercase text-[9px] font-bold">{l}</p>
                        <p className="font-bold text-gray-800 mt-0.5">{v||'—'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status + Payment controls */}
              {!locked && (
                <>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Update Status</p>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTS.map(s => (
                        <button key={s} onClick={() => {
                          if (s === 'completed' && ['Cash for Cars','Free Car Removal'].includes(selected.service_type)) {
                            markCompleted(selected);
                          } else {
                            updateBooking(selected.id, { status: s });
                          }
                        }}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all
                            ${selected.status===s
                              ? `${STATUS_COLORS[s]} border-transparent`
                              : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`}>
                          {s.replace('_',' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                  {selected.status==='confirmed' && (
                    <button onClick={() => updateBooking(selected.id, { status:'in_progress' })}
                      className="w-full bg-orange-500 text-white py-3 rounded-xl font-bold hover:bg-orange-600 flex items-center justify-center gap-2">
                      🚛 Start Job — Mark In Progress
                    </button>
                  )}
                  {selected.status==='in_progress' && (
                    <button onClick={() => markCompleted(selected)}
                      className="w-full bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 flex items-center justify-center gap-2">
                      <Check className="w-4 h-4" /> Mark Job Completed
                    </button>
                  )}
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Payment</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {[['unpaid','Unpaid'],['paid','Paid'],['n/a','N/A']].map(([v,l]) => (
                        <button key={v} onClick={() => updateBooking(selected.id, { payment_status: v })}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all
                            ${selected.payment_status===v ? 'bg-apex-blue-700 border-apex-blue-700 text-white' : 'border-gray-200 bg-white text-gray-600'}`}>{l}</button>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[['cash','💵 Cash'],['card','💳 Card'],['payid','📱 PayID'],['bank_transfer','🏦 Bank Transfer']].map(([v,l]) => (
                        <button key={v} onClick={() => updateBooking(selected.id, { payment_method: v })}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all
                            ${selected.payment_method===v ? 'bg-green-600 border-green-600 text-white' : 'border-gray-200 bg-white text-gray-600'}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Editable charge */}
              {!['Cash for Cars','Free Car Removal'].includes(selected.service_type) && (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">Job Charge</p>
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-black text-gray-400">$</span>
                    <input
                      type="number"
                      defaultValue={selected.charge || ''}
                      placeholder="Enter amount charged"
                      className="flex-1 px-3 py-2.5 border-2 border-amber-200 rounded-xl text-xl font-black focus:outline-none focus:border-amber-500"
                      onBlur={async e => {
                        const val = Number(e.target.value);
                        if (!isNaN(val) && val !== selected.charge) {
                          await updateBooking(selected.id, { charge: val });
                          showToast(`✅ Charge updated to $${val.toLocaleString()}`);
                        }
                      }}
                    />
                  </div>
                  {!selected.charge && (
                    <p className="text-xs text-amber-600 mt-1.5">⚠️ No charge set — enter the amount before creating an invoice</p>
                  )}
                </div>
              )}

              {/* Photos */}
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">📸 Job Photos</p>
                <ImageStages images={selected.images || {}} bookingId={selected.id}
                  onRefresh={async () => {
                    load();
                    try {
                      const r = await api.get(`/api/bookings/${selected.id}`);
                      setSelected(r.data);
                    } catch {}
                  }} />
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 pt-2 border-t border-gray-100 flex-wrap">
                {selected.invoice_id ? (
                  <div className="flex gap-2 flex-1">
                    <button onClick={async () => {
                      try {
                        const res = await api.get(`/api/invoices/${selected.invoice_id}`);
                        setViewingExistingInv(res.data);
                      } catch { alert('Could not load invoice — try refreshing.'); }
                    }}
                      className="flex-1 bg-purple-600 text-white py-3 rounded-xl font-bold hover:bg-purple-700 flex items-center justify-center gap-2">
                      <Eye className="w-4 h-4" /> View Invoice
                    </button>
                    <button onClick={() => setShowInvoice(true)}
                      className="px-3 py-3 border-2 border-purple-200 text-purple-600 rounded-xl font-bold hover:bg-purple-50" title="Regenerate Invoice">
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setShowInvoice(true)}
                    className="flex-1 bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 flex items-center justify-center gap-2">
                    <FileText className="w-4 h-4" /> Create Invoice
                  </button>
                )}
                {!locked && (
                  <button onClick={() => deleteBooking(selected.id)}
                    className="px-4 py-3 border-2 border-red-200 text-red-500 rounded-xl font-bold hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => setSelected(null)}
                  className="px-6 py-3 bg-apex-blue-700 text-white rounded-xl font-bold hover:bg-apex-blue-600">
                  Close
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Invoice Creator */}
      <InvoiceCreator open={showInvoice} onClose={() => setShowInvoice(false)}
        booking={selected} companySettings={companySettings}
        onSave={() => { setShowInvoice(false); load(); loadTabCounts(); }} />

      {/* Car Purchase Prompt — appears when Cash for Cars job is completed */}
      {carPurchasePrompt && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-5">
              <p className="text-white/70 text-xs font-bold uppercase tracking-wider">Job Completed 🎉</p>
              <h2 className="text-white font-black text-xl mt-0.5">Record as Car Purchase?</h2>
              <p className="text-white/60 text-sm mt-1">
                {carPurchasePrompt.customer_name} — {[carPurchasePrompt.vehicle_year, carPurchasePrompt.vehicle_make, carPurchasePrompt.vehicle_model].filter(Boolean).join(' ') || 'Vehicle'}
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700">
                This will add the vehicle to your <strong>Car Purchases</strong> ledger, updating your stock and profit calculations. Photos from the booking will be carried over automatically.
              </div>

              {/* Amount paid */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Amount Paid to Customer ($) *</label>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-black text-gray-400">$</span>
                  <input
                    type="number"
                    value={cpForm.paid}
                    onChange={e => setCpForm(f => ({ ...f, paid: e.target.value }))}
                    placeholder={carPurchasePrompt.charge || '0'}
                    className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-lg font-black focus:outline-none focus:border-green-500"
                  />
                </div>
              </div>

              {/* Condition */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Vehicle Condition</label>
                <select
                  value={cpForm.condition}
                  onChange={e => setCpForm(f => ({ ...f, condition: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-green-500"
                >
                  <option value="">Select condition</option>
                  {['Excellent','Good','Fair','Poor','Scrap'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Internal Notes (optional)</label>
                <textarea
                  value={cpForm.notes}
                  onChange={e => setCpForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  placeholder="e.g. Needs new tyres, engine runs well..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:border-green-500"
                />
              </div>

              {/* Photos preview */}
              {(() => {
                const allPhotos = Object.values(carPurchasePrompt.images || {}).flat().filter(i => i.url);
                return allPhotos.length > 0 ? (
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase mb-1.5">{allPhotos.length} photos will be carried over</p>
                    <div className="flex gap-2 overflow-x-auto">
                      {allPhotos.slice(0, 5).map((img, i) => (
                        <img key={i} src={img.url} alt="" className="w-16 h-12 object-cover rounded-lg shrink-0" onError={e => e.target.style.display='none'} />
                      ))}
                      {allPhotos.length > 5 && <div className="w-16 h-12 bg-gray-100 rounded-lg shrink-0 flex items-center justify-center text-xs text-gray-400 font-bold">+{allPhotos.length - 5}</div>}
                    </div>
                  </div>
                ) : null;
              })()}

              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setCarPurchasePrompt(null)}
                  className="flex-1 border-2 border-gray-200 text-gray-600 py-3 rounded-xl font-bold hover:bg-gray-50"
                >
                  Skip — Don't Record
                </button>
                <button
                  onClick={saveCarPurchase}
                  disabled={!cpForm.paid}
                  className="flex-1 bg-green-600 text-white py-3 rounded-xl font-black hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Car className="w-4 h-4" /> Record Purchase
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {viewingExistingInv && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget) setViewingExistingInv(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <div>
                <h2 className="font-black text-apex-blue-700 text-lg">{viewingExistingInv.number}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{viewingExistingInv.created_at?.split('T')[0]} · Due: {viewingExistingInv.due_date || 'On receipt'}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold px-3 py-1 rounded-full capitalize ${viewingExistingInv.status==='paid'?'bg-green-100 text-green-800':'bg-blue-100 text-blue-800'}`}>
                  {viewingExistingInv.status}
                </span>
                <button onClick={() => setViewingExistingInv(null)}><X className="w-5 h-5 text-gray-400" /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Bill To</p>
                <p className="font-bold text-gray-900">{viewingExistingInv.client_name}</p>
                {viewingExistingInv.client_email && <p className="text-sm text-gray-500">{viewingExistingInv.client_email}</p>}
              </div>
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50"><th className="text-left p-3 text-xs text-gray-400 uppercase font-bold">Description</th><th className="text-right p-3 text-xs text-gray-400 uppercase font-bold">Amount</th></tr></thead>
                <tbody>{(viewingExistingInv.items||[]).map((it,i) => <tr key={i} className="border-t border-gray-100"><td className="p-3 text-gray-700">{it.desc}</td><td className="p-3 text-right font-semibold">${(it.total||it.rate||0).toLocaleString()}</td></tr>)}</tbody>
                <tfoot><tr className="bg-apex-blue-700 text-white"><td className="p-3 font-bold rounded-bl-xl">TOTAL (incl. GST)</td><td className="p-3 text-right font-black text-xl rounded-br-xl">${(viewingExistingInv.amount||0).toLocaleString()}</td></tr></tfoot>
              </table>
              {viewingExistingInv.signature && (
                <div className="border-t pt-3">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-2">Customer Signature</p>
                  <img src={viewingExistingInv.signature} alt="Signature" className="h-14 border-b-2 border-apex-blue-700" />
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => {
                  const co = companySettings;
                  const win = window.open('', '_blank', 'width=900,height=700');
                  win.document.write(`<!DOCTYPE html><html><head><title>${viewingExistingInv.number}</title><style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;padding:40px}.total{background:#0D2B6B;color:#fff;font-weight:900}@media print{body{print-color-adjust:exact}}</style></head><body><h1 style="color:#0D2B6B">${co?.company_name||'APEX TOWING'}</h1><p>${co?.company_phone||''} · ${co?.company_email||''}</p><hr style="margin:16px 0;border-color:#0D2B6B;border-width:2px"><p><b>Bill To:</b> ${viewingExistingInv.client_name}</p><table width="100%" style="margin-top:16px;border-collapse:collapse"><tr style="background:#f8f9fc"><th style="text-align:left;padding:8px">Description</th><th style="text-align:right;padding:8px">Amount</th></tr>${(viewingExistingInv.items||[]).map(i=>`<tr><td style="padding:8px;border-bottom:1px solid #eee">${i.desc}</td><td style="padding:8px;border-bottom:1px solid #eee;text-align:right">$${(i.total||0).toLocaleString()}</td></tr>`).join('')}<tr class="total"><td style="padding:10px">TOTAL</td><td style="padding:10px;text-align:right">$${(viewingExistingInv.amount||0).toLocaleString()}</td></tr></table>${viewingExistingInv.signature?`<div style="margin-top:20px"><p><b>Customer Signature:</b></p><img src="${viewingExistingInv.signature}" style="height:60px;border-bottom:2px solid #0D2B6B"/></div>`:''}<p style="text-align:center;margin-top:24px;color:#888;font-size:12px">${co?.invoice_notes||''}</p></body></html>`);
                  win.document.close(); setTimeout(() => win.print(), 500);
                }} className="flex-1 bg-apex-blue-700 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2"><Printer className="w-4 h-4" /> Print</button>
                <button onClick={() => setViewingExistingInv(null)} className="px-6 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Booking Modal */}
      <Modal open={showCreate} onClose={() => { setShowCreate(false); setCreateStep(1); }} title="" maxWidth="max-w-lg">
        <div className="-mx-6 -mt-6 bg-apex-blue-700 px-6 py-5 rounded-t-2xl mb-6">
          <p className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">New Booking</p>
          <div className="flex items-center">
            {['Customer','Service & Location','Vehicle'].map((l, i) => {
              const done = createStep > i+1, act = createStep === i+1;
              return (
                <React.Fragment key={l}>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black
                      ${done?'bg-green-400 text-apex-blue-700':act?'bg-apex-yellow-500 text-apex-blue-700':'bg-white/20 text-white/40'}`}>
                      {done?'✓':i+1}
                    </div>
                    <span className={`text-xs whitespace-nowrap ${act?'text-white font-bold':'text-white/40'}`}>{l}</span>
                  </div>
                  {i < 2 && <div className="flex-1 h-px bg-white/20 mx-3 min-w-[12px]" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {createStep===1 && (
          <div className="space-y-4">
            <Field label="Full Name" required><input value={form.customer_name} onChange={sf('customer_name')} className={inputCls} placeholder="John Smith" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone" required><input value={form.customer_phone} onChange={sf('customer_phone')} className={inputCls} placeholder="07 0000 0000" /></Field>
              <Field label="Email"><input value={form.customer_email} onChange={sf('customer_email')} className={inputCls} placeholder="john@email.com" /></Field>
            </div>
            <button onClick={() => form.customer_name && form.customer_phone && setCreateStep(2)}
              className={`w-full py-3 rounded-xl font-bold text-white ${form.customer_name&&form.customer_phone?'bg-apex-blue-700 hover:bg-apex-blue-600':'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
              Next: Service →
            </button>
          </div>
        )}

        {createStep===2 && (
          <div className="space-y-3">
            <Field label="Service Type" required>
              <select value={form.service_type} onChange={sf('service_type')} className={selectCls}>
                <option value="">Select service...</option>
                {['Free Car Removal','Cash for Cars','Standard Towing','Emergency Towing','Heavy Machinery Transport','Long Distance Transport','B2B Towing'].map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Pickup Address" required><input value={form.pickup_address} onChange={sf('pickup_address')} className={inputCls} /></Field>
              <Field label="Pickup Suburb"><input value={form.pickup_suburb} onChange={sf('pickup_suburb')} className={inputCls} /></Field>
              <Field label="Drop-off Address"><input value={form.dropoff_address} onChange={sf('dropoff_address')} className={inputCls} /></Field>
              <Field label="Drop-off Suburb"><input value={form.dropoff_suburb} onChange={sf('dropoff_suburb')} className={inputCls} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date *"><input type="date" value={form.scheduled_date} onChange={sf('scheduled_date')} className={inputCls} /></Field>
              <Field label="Time"><TimePicker value={form.scheduled_time} onChange={v => setForm(f => ({ ...f, scheduled_time: v }))} /></Field>
              <Field label="Charge ($)"><input type="number" value={form.charge} onChange={sf('charge')} className={inputCls} placeholder="0" /></Field>
              <Field label="Payment Method">
                <select value={form.payment_method} onChange={sf('payment_method')} className={selectCls}>
                  <option value="">Select</option>
                  <option value="cash">Cash</option><option value="card">Card</option>
                  <option value="payid">PayID</option><option value="bank_transfer">Bank Transfer</option>
                </select>
              </Field>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setCreateStep(1)} className="px-4 py-3 border-2 border-gray-200 rounded-xl font-bold text-gray-500">←</button>
              <button onClick={() => form.service_type && form.pickup_address && setCreateStep(3)}
                className={`flex-1 py-3 rounded-xl font-bold text-white ${form.service_type&&form.pickup_address?'bg-apex-blue-700 hover:bg-apex-blue-600':'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                Next: Vehicle →
              </button>
            </div>
          </div>
        )}

        {createStep===3 && (
          <div className="space-y-3">
            <div className="bg-apex-blue-50 rounded-xl px-4 py-3 text-xs text-apex-blue-700 font-medium">Vehicle details can be added or updated later from the booking view.</div>
            <div className="grid grid-cols-2 gap-3">
              {[['Make','vehicle_make','Toyota'],['Model','vehicle_model','Corolla'],
                ['Year','vehicle_year','2020'],['Color','vehicle_color','White'],
                ['Rego','vehicle_rego','ABC 123']].map(([l,k,ph]) => (
                <Field key={k} label={l}><input value={form[k]} onChange={sf(k)} className={inputCls} placeholder={ph} /></Field>
              ))}
              <Field label="Condition">
                <select value={form.vehicle_condition} onChange={sf('vehicle_condition')} className={selectCls}>
                  <option value="">Select</option>
                  <option value="running">Running</option>
                  <option value="not_running">Not Running</option>
                  <option value="accident_damaged">Accident Damaged</option>
                </select>
              </Field>
            </div>
            <Field label="Notes"><textarea value={form.notes} onChange={sf('notes')} className={`${inputCls} resize-none`} rows={2} /></Field>
            <div className="flex gap-3">
              <button onClick={() => setCreateStep(2)} className="px-4 py-3 border-2 border-gray-200 rounded-xl font-bold text-gray-500">←</button>
              <button onClick={createBooking}
                className="flex-1 bg-apex-yellow-500 text-apex-blue-700 py-3 rounded-xl font-black hover:bg-apex-yellow-400 flex items-center justify-center gap-2">
                <Check className="w-5 h-5" /> Confirm & Create Booking
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}